import get from 'lodash/get';

import {
  getCommunityScopeOptions,
  getCommunityStatusOptions,
  getEventsCategories,
  getReservationMultiStatusOptions,
} from 'shared/constants';
import i18n from 'shared/lib/i18n';

import { getSelectValue, sortByKey } from '../../utils';
import { getDoorOpeningMethods } from '../Building/utils';
import { getEventsStatusTypes } from '../Events/utils';
import { formatSelect, formatTextSelect } from '../Forms/utils';

import { getCallStatusOptions } from '../RemoteDoorman/constants';
import { getFeedbackTypes } from '../ServiceRequest/constants';

import { limitOptions } from '../../staticData';

export const pmcFilter = (listParams, pmcs, setOnChange) => {
  const key = 'pmc';
  const options = [
    ...formatSelect(
      Object.values(pmcs).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];
  const value = getSelectValue(listParams?.[key], options);
  let filter = {
    key,
    defaultValue: null,
    placeholder: i18n.t('table.selectPmc'),
    isClearable: true,
    options,
    value,
  };
  if (setOnChange) {
    filter = { ...filter, setOnChange };
  }
  return filter;
};

export const buildingFilter = (
  listParams,
  buildings,
  setOnChange,
  multi = false,
  key = 'building',
) => {
  const options = [
    ...formatSelect(
      Object.values(buildings).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];
  const value = getSelectValue(listParams?.[key], options);
  let filter = {
    key,
    defaultValue: null,
    placeholder: i18n.t('title.selectBuilding'),
    isClearable: true,
    multi,
    options,
    value,
  };
  if (setOnChange) {
    filter = { ...filter, setOnChange };
  }
  return filter;
};

export const multiBuildingFilter = (listParams, buildings) => {
  const key = 'available_buildings';

  const options = [
    ...formatSelect(
      Object.values(buildings).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];

  const selectedStatuses = listParams?.[key]
    ?.toString()
    ?.split(',')
    ?.filter(Boolean);

  const value = selectedStatuses?.map((value) =>
    getSelectValue(value, options),
  );

  const filter = {
    key,
    defaultValue: null,
    placeholder: i18n.t('title.selectBuilding_s'),
    isClearable: true,
    multi: true,
    isMultiWithCheckbox: true,
    options,
    value,
  };

  return filter;
};

export const unitFilter = (listParams, units) => {
  const key = 'unit';
  const options = [
    ...formatSelect(Object.values(units).sort((a, b) => a.order - b.order)),
  ];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: null,
    placeholder: i18n.t('table.selectUnit'),
    isClearable: true,
    options,
    value,
  };
};

export const doorFilter = (listParams, doors) => {
  const key = 'door';
  const options = [...formatSelect(Object.values(doors))];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectDoor'),
    isClearable: true,
    options,
    value,
  };
};

export const actionFilter = (listParams, options) => {
  const key = 'action';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.linkedActions'),
    isClearable: true,
    options,
    value,
  };
};

export const approvalScopeFilter = (listParams) => {
  const key = 'approval_scope';
  const options = getCommunityScopeOptions();
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.allStatuses'),
    isClearable: true,
    options,
    value,
  };
};

export const sourceFilter = (listParams) => {
  const key = 'source';
  const options = getDoorOpeningMethods();
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('title.openedUsing'),
    isClearable: true,
    options,
    value,
  };
};

export const limitFilter = (listParams) => {
  const key = 'limit';
  const options = [...formatSelect(Object.values(limitOptions()))];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.itemsPerPage'),
    isClearable: false,
    options,
    sortAlphabetically: false,
    value,
  };
};

export const agentFilter = (listParams, options, listParamsFormatter) => {
  const key = 'cc_agent';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectCCAgent'),
    isClearable: true,
    options,
    value,
    listParamsFormatter,
  };
};

export const callStatusFilter = (listParams) => {
  const key = 'status';
  const options = getCallStatusOptions();
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectCallStatus'),
    isClearable: true,
    options,
    value,
  };
};

export const typeFilter = (listParams, options) => {
  const key = 'type';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectTypes'),
    isClearable: true,
    options,
    value,
  };
};

export const accessCardsBatchFilter = (listParams, batchOptions) => {
  const key = 'access_card_batch';
  const options = [...formatSelect(batchOptions, 'name', 'id')];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('title.batchName'),
    isClearable: true,
    options,
    value,
  };
};

export const actionsLogNamesFilter = (listParams, actionsLogNames) => {
  const key = 'action_name';
  const options = [...formatTextSelect(actionsLogNames)];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.filterByAction'),
    isClearable: true,
    options,
    value,
  };
};

export const statusFilter = (listParams, serviceRequestsCounts) => {
  const serviceRequestPendingResolved = [
    {
      id: 'pending',
      name: i18n.t('status.pending'),
    },
    {
      id: 'resolved',
      name: i18n.t('status.resolved'),
    },
  ];

  const options = serviceRequestsCounts
    ? [...formatSelect(Object.values(serviceRequestPendingResolved))].map(
        (option) => {
          if (!serviceRequestsCounts[option.value]) {
            return option;
          }
          const label = `${option.label} (${
            serviceRequestsCounts[option.value]
          })`;
          return {
            ...option,
            label,
          };
        },
      )
    : [...formatSelect(Object.values(serviceRequestPendingResolved))];
  const value = options.find((option) => listParams?.status === option.value);

  return {
    key: 'status',
    defaultValue: get(listParams, 'status', null),
    placeholder: i18n.t('table.selectStatus'),
    isClearable: true,
    options,
    value,
  };
};

export const dateRangeTypeFilter = (dateRangeTypeOptions, dateRangeType) => {
  const key = 'dateRangeType';
  const options = [...dateRangeTypeOptions];
  const value = getSelectValue(dateRangeType, options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.dateRangeType'),
    isClearable: false,
    options,
    value,
  };
};

export const visitorStatusFilter = (listParams) => {
  const key = 'status';
  const options = [
    {
      value: 'active',
      label: i18n.t('visitor.active'),
    },
    {
      value: 'expired',
      label: i18n.t('visitor.expired'),
    },
  ];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectVisitorStatus'),
    isClearable: true,
    options,
    value,
  };
};

export const roleFilter = (listParams, options) => {
  const key = 'role';
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectRole'),
    isClearable: true,
    options,
    value,
  };
};

export const commonAreaFilter = (listParams) => {
  const key = 'common_area';
  const options = [{ label: i18n.t('table.commonArea'), value: 'True' }]; // the API required to start with a capital letter in this case
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.isCommonArea'),
    isClearable: true,
    options,
    value,
  };
};

export const amenityMultiStatusFilter = (listParams) => {
  const key = 'approval_statuses';
  const options = getReservationMultiStatusOptions();
  const selectedStatuses = listParams?.[key]
    ?.toString()
    ?.split(',')
    ?.filter(Boolean);

  const value = selectedStatuses?.map((value) =>
    getSelectValue(value, options),
  );

  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('title.status'),
    isClearable: true,
    options,
    value,
    multi: true,
    isMultiWithCheckbox: true,
  };
};

export const amenityFilter = (listParams, amenities) => {
  const key = 'amenity';
  const options = [...formatSelect(amenities)];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('title.selectAmenity'),
    isClearable: true,
    options,
    value,
  };
};

export const feedbackFilter = (listParams) => {
  const key = 'positive_feedback';
  const options = [...getFeedbackTypes()];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectFeedback'),
    isClearable: true,
    options,
    value,
  };
};

export const openDeliveryFilter = (listParams) => {
  const key = 'sign_out_state';
  const options = [
    {
      label: i18n.t('title.openDeliveries'),
      value: 'pending',
    },
    {
      label: i18n.t('title.closedDeliveries'),
      value: 'past',
    },
  ];
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.deliveryStatus'),
    isClearable: true,
    options,
    value,
  };
};

export const communityFilter = (listParams, communities, setOnChange) => {
  const key = 'community';
  const options = [
    ...formatSelect(
      Object.values(communities).sort((a, b) => sortByKey(a, b, 'name')),
    ),
  ];
  const value = getSelectValue(listParams?.[key], options);
  let filter = {
    key,
    defaultValue: null,
    placeholder: i18n.t('table.selectCommunity'),
    isClearable: true,
    options,
    value,
  };
  if (setOnChange) {
    filter = { ...filter, setOnChange };
  }
  return filter;
};

export const communityStatusFilter = (listParams) => {
  const key = 'approval_status';
  const options = getCommunityStatusOptions();
  const value = getSelectValue(listParams?.[key], options);

  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.selectStatus'),
    isClearable: true,
    options,
    value,
  };
};

export const srIdsFilter = (listParams, ids) => {
  const key = 'carson_service_requests_ids';
  const options = [...formatTextSelect(ids)];

  // an array of SR ids is represented as a comma separated list of ids
  // for example carson_service_requests_ids=1,2,3
  // to map each id value the string is splitted by the comma separator
  const selectedIds = listParams?.[key]?.split(',')?.filter(Boolean);

  // multi select requires an array of values
  // with the [{ label, value }] structure
  const value = selectedIds?.map((value) => getSelectValue(value, options));

  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.srId'),
    isClearable: true,
    options,
    value,
    multi: true,
    sortAlphabetically: false,
  };
};

export const eventsMultiCategoryFilter = (listParams) => {
  const key = 'category';
  const options = getEventsCategories();
  const selectedStatuses = listParams?.[key]
    ?.toString()
    ?.split(',')
    ?.filter(Boolean);

  const value = selectedStatuses?.map((value) =>
    getSelectValue(value, options),
  );

  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('title.category'),
    isClearable: true,
    options,
    value,
    multi: true,
    isMultiWithCheckbox: true,
    width: '30rem',
  };
};

export const eventStatusFilter = (listParams) => {
  const key = 'upcoming';
  const options = getEventsStatusTypes();
  const value = getSelectValue(listParams?.[key], options);
  return {
    key,
    defaultValue: value,
    placeholder: i18n.t('table.eventStatus'),
    isClearable: true,
    options,
    value,
  };
};
